import { Add as AddIcon, Close as CloseIcon, Delete as DeleteIcon, Edit as EditIcon, Category as EmptyIcon, Search as SearchIcon, Warning as WarningIcon } from "@mui/icons-material"
import { Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Pagination, Paper, Select, Stack, TextField, Typography } from "@mui/material"
import axios from "axios"
import { useEffect, useState } from "react"

export default function HomeworkSubjects({ snackbar }) {
	const apiUrl = process.env.REACT_APP_API

	// ─── List State ───────────────────────────────────────────────────────────
	const [subjects, setSubjects] = useState([])
	const [loading, setLoading] = useState(true)
	const [searchInput, setSearchInput] = useState("")
	const [search, setSearch] = useState("")
	const [statusFilter, setStatusFilter] = useState("all")
	const [page, setPage] = useState(1)
	const [pagination, setPagination] = useState({
		current_page: 1,
		limit: 10,
		total_records: 0,
		total_pages: 1,
	})
	const [stats, setStats] = useState({ active: 0, inactive: 0, total: 0 })

	// ─── Form / Dialog State ──────────────────────────────────────────────────
	const emptyForm = { subject_name: "", subject_status: "active" }
	const [open, setOpen] = useState("") // "" | "add" | "edit" | "delete"
	const [action, setAction] = useState("") // "" | "add" | "update" | "delete"
	const [form, setForm] = useState(emptyForm)
	const [selectedSubject, setSelectedSubject] = useState(null)

	// ─── Fetch ────────────────────────────────────────────────────────────────
	const getSubjects = () => {
		setLoading(true)
		const params = new URLSearchParams()
		if (search) params.append("search", search)
		if (statusFilter !== "all") params.append("status", statusFilter)
		params.append("page", page)

		axios
			.get(`${apiUrl}/franchise/homework/subject/getall?${params.toString()}`)
			.then((res) => {
				if (res.data.status === true) {
					setSubjects(res.data.data || [])
					if (res.data.pagination) setPagination(res.data.pagination)
					if (res.data.stats) setStats(res.data.stats)
				} else {
					setSubjects([])
				}
			})
			.catch(() => setSubjects([]))
			.finally(() => setLoading(false))
	}

	useEffect(() => {
		getSubjects()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [search, statusFilter, page])

	const handleSearchSubmit = (e) => {
		if (e.key === "Enter") {
			setSearch(searchInput)
			setPage(1)
		}
	}

	const handlePageChange = (e, value) => {
		setPage(value)
		window.scrollTo({ top: 0, behavior: "smooth" })
	}

	// ─── Add ──────────────────────────────────────────────────────────────────
	const openAdd = () => {
		setForm(emptyForm)
		setOpen("add")
	}

	const handleAdd = () => {
		setAction("add")
		axios
			.post(`${apiUrl}/franchise/homework/subject/add`, { subject_name: form.subject_name }, { headers: { "Content-Type": "application/json" } })
			.then((res) => {
				snackbar && snackbar(res.data.message || "Subject created")
				if (res.data.status === true) {
					setOpen("")
					setPage(1)
					getSubjects()
				}
			})
			.finally(() => setAction(""))
	}

	// ─── Edit ─────────────────────────────────────────────────────────────────
	const openEdit = (subject) => {
		setSelectedSubject(subject)
		setForm({
			subject_name: subject.subject_name,
			subject_status: subject.subject_status,
		})
		setOpen("edit")
	}

	const handleUpdate = () => {
		setAction("update")
		axios
			.post(`${apiUrl}/franchise/homework/subject/update`, { subjectid: selectedSubject.subjectid, ...form }, { headers: { "Content-Type": "application/json" } })
			.then((res) => {
				snackbar && snackbar(res.data.message || "Subject updated")
				if (res.data.status === true) {
					setOpen("")
					getSubjects()
				}
			})
			.finally(() => setAction(""))
	}

	// ─── Delete ───────────────────────────────────────────────────────────────
	const openDelete = (subject) => {
		setSelectedSubject(subject)
		setOpen("delete")
	}

	const handleDelete = () => {
		setAction("delete")
		axios
			.post(`${apiUrl}/franchise/homework/subject/delete`, { subjectid: selectedSubject.subjectid }, { headers: { "Content-Type": "application/json" } })
			.then((res) => {
				snackbar && snackbar(res.data.message || "Subject deleted")
				if (res.data.status === true) {
					setOpen("")
					getSubjects()
				}
			})
			.finally(() => setAction(""))
	}

	// ─── Helpers ──────────────────────────────────────────────────────────────
	const formatDate = (d) => {
		if (!d) return ""
		return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
	}

	const statusMeta = (status) => {
		switch (status) {
			case "active":
				return { label: "Active", color: "success", border: "success.main" }
			default:
				return { label: "Inactive", color: "default", border: "grey.400" }
		}
	}

	// ─── Render ───────────────────────────────────────────────────────────────
	return (
		<Box className="content" pt={3} pb={5}>
			<Container maxWidth="lg">
				{/* ── Page Header ─────────────────────────────────── */}
				<Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2} mb={3}>
					<Box>
						<Typography variant="h5" fontWeight={700}>
							Home Work Subjects
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Manage the subject taxonomy used for home work assignments
						</Typography>
					</Box>
					<Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ borderRadius: 2 }}>
						New Subject
					</Button>
				</Stack>

				{/* ── Summary Strip ───────────────────────────────── */}
				<Stack direction="row" spacing={2} mb={3} flexWrap="wrap" useFlexGap>
					{[
						{ key: "all", label: "Total Subjects", value: stats.total, color: "text.primary" },
						{ key: "active", label: "Active", value: stats.active, color: "success.main" },
						{ key: "inactive", label: "Inactive", value: stats.inactive, color: "text.secondary" },
					].map((card) => (
						<Paper
							key={card.key}
							variant="outlined"
							onClick={() => {
								setStatusFilter(card.key)
								setPage(1)
							}}
							sx={{
								px: 2.5,
								py: 1.5,
								borderRadius: 2,
								minWidth: 140,
								cursor: "pointer",
								borderWidth: statusFilter === card.key ? 2 : 1,
								borderColor: statusFilter === card.key ? card.color : "divider",
								transition: "border-color .15s, box-shadow .15s",
								"&:hover": { boxShadow: 2 },
							}}
						>
							<Typography variant="caption" color="text.secondary">
								{card.label}
							</Typography>
							<Typography variant="h6" fontWeight={700} color={card.color}>
								{card.value}
							</Typography>
						</Paper>
					))}
				</Stack>

				{/* ── Filters ─────────────────────────────────────── */}
				<Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
					<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
						<TextField
							fullWidth
							size="small"
							placeholder="Search subject..."
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
							onKeyDown={handleSearchSubmit}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon fontSize="small" color="action" />
									</InputAdornment>
								),
							}}
						/>
						<FormControl size="small" sx={{ minWidth: 160 }}>
							<InputLabel>Status</InputLabel>
							<Select
								label="Status"
								value={statusFilter}
								onChange={(e) => {
									setStatusFilter(e.target.value)
									setPage(1)
								}}
							>
								<MenuItem value="all">All Status</MenuItem>
								<MenuItem value="active">Active</MenuItem>
								<MenuItem value="inactive">Inactive</MenuItem>
							</Select>
						</FormControl>
						<Button
							variant="outlined"
							color="inherit"
							onClick={() => {
								setSearchInput("")
								setSearch("")
								setStatusFilter("all")
								setPage(1)
							}}
						>
							Clear
						</Button>
					</Stack>
				</Paper>

				{/* ── Subject Cards ───────────────────────────────── */}
				{loading ? (
					<Box textAlign="center" py={8}>
						<CircularProgress />
					</Box>
				) : subjects.length === 0 ? (
					<Paper variant="outlined" sx={{ py: 8, textAlign: "center", borderRadius: 2, borderStyle: "dashed" }}>
						<EmptyIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
						<Typography variant="h6" color="text.secondary">
							No subjects found
						</Typography>
						<Typography variant="body2" color="text.secondary" mb={2}>
							Try adjusting your search, or create a new subject to get started
						</Typography>
						<Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
							Create Subject
						</Button>
					</Paper>
				) : (
					<Grid container spacing={2}>
						{subjects.map((row) => (
							<Grid item xs={12} sm={6} md={4} key={row.subjectid}>
								<Paper
									variant="outlined"
									sx={{
										borderRadius: 3,
										overflow: "hidden",
										height: "100%",
										display: "flex",
										flexDirection: "column",
										borderLeft: "5px solid",
										borderLeftColor: statusMeta(row.subject_status).border,
										transition: "box-shadow .2s, transform .2s",
										"&:hover": { boxShadow: 4, transform: "translateY(-2px)" },
									}}
								>
									<Box p={2} flexGrow={1}>
										<Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
											<Typography fontWeight={700} noWrap maxWidth={180}>
												{row.subject_name}
											</Typography>
											<Chip size="small" label={statusMeta(row.subject_status).label} color={statusMeta(row.subject_status).color} />
										</Stack>
										<Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
											{row.subjectid} · Created {formatDate(row.created_at)}
										</Typography>
									</Box>

									<Divider />
									<Stack direction="row">
										<Button fullWidth size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => openEdit(row)} sx={{ borderRadius: 0, py: 1 }}>
											Edit
										</Button>
										<Divider orientation="vertical" flexItem />
										<Button fullWidth size="small" color="error" startIcon={<DeleteIcon fontSize="small" />} onClick={() => openDelete(row)} sx={{ borderRadius: 0, py: 1 }}>
											Delete
										</Button>
									</Stack>
								</Paper>
							</Grid>
						))}
					</Grid>
				)}

				{/* ── Pagination ──────────────────────────────────── */}
				{pagination.total_pages > 1 && (
					<Box mt={4} display="flex" justifyContent="center">
						<Pagination count={pagination.total_pages} page={page} onChange={handlePageChange} color="primary" showFirstButton showLastButton />
					</Box>
				)}
			</Container>

			{/* ── Add / Edit Dialog ───────────────────────────────── */}
			<Dialog open={open === "add" || open === "edit"} fullWidth maxWidth="xs" onClose={() => setOpen("")}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					{open === "add" ? "Create New Subject" : "Edit Subject"}
					<IconButton size="small" onClick={() => setOpen("")}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>
				<Divider />
				<DialogContent>
					<Stack spacing={2} mt={1}>
						<TextField label="Subject Name" fullWidth autoFocus value={form.subject_name} onChange={(e) => setForm({ ...form, subject_name: e.target.value })} />
						{open === "edit" && (
							<FormControl fullWidth>
								<InputLabel>Status</InputLabel>
								<Select label="Status" value={form.subject_status} onChange={(e) => setForm({ ...form, subject_status: e.target.value })}>
									<MenuItem value="active">Active</MenuItem>
									<MenuItem value="inactive">Inactive</MenuItem>
								</Select>
							</FormControl>
						)}
					</Stack>
				</DialogContent>
				<Divider />
				<DialogActions sx={{ p: 2 }}>
					<Button color="inherit" onClick={() => setOpen("")}>
						Cancel
					</Button>
					<Button variant="contained" disabled={action === "add" || action === "update" || !form.subject_name} onClick={open === "add" ? handleAdd : handleUpdate}>
						{action === "add" || action === "update" ? <CircularProgress size={20} color="inherit" /> : open === "add" ? "Create Subject" : "Save Changes"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* ── Delete Confirm Dialog ───────────────────────────── */}
			<Dialog open={open === "delete"} fullWidth maxWidth="xs" onClose={() => setOpen("")}>
				<DialogContent>
					<Box textAlign="center">
						<WarningIcon sx={{ width: 64, height: 64 }} color="error" />
						<Typography color="error" fontSize={20} fontWeight={600} mt={1}>
							Delete this subject?
						</Typography>
						<Typography color="text.secondary" fontSize={13} mb={2}>
							<strong>{selectedSubject?.subject_name}</strong> will be permanently removed. This action cannot be undone.
						</Typography>
					</Box>
					<Stack spacing={1}>
						<Button variant="contained" color="error" disabled={action === "delete"} onClick={handleDelete}>
							{action === "delete" ? <CircularProgress size={20} color="inherit" /> : "Yes, Delete"}
						</Button>
						<Button variant="outlined" color="inherit" onClick={() => setOpen("")}>
							Cancel
						</Button>
					</Stack>
				</DialogContent>
			</Dialog>
		</Box>
	)
}
